"use client"

import { Conversation, FormJourneyItem, JourneySelectOption, LeadForm } from "@plura/shared/types";
// Screens
import { useEffect, useRef, useState } from "react";
import { cn } from "@plura/shared/utils";
import AddressScreen from "./_conversationScreens/AddressScreen";
import ContactInfoScreen from "./_conversationScreens/ContactInfoScreen";
import ConversationExitScreen from "./_conversationScreens/ConversationExitScreen";
import ConversationRedirectScreen from "./_conversationScreens/ConversationRedirectScreen";
import ConversationWelcomeScreen from "./_conversationScreens/ConversationWelcomeScreen";
import DateScreen from "./_conversationScreens/DateScreen";
import DropdownScreen from "./_conversationScreens/DropdownScreen";
import EmailScreen from "./_conversationScreens/EmailScreen";
import FileScreen from "./_conversationScreens/FileScreen";
import LongTextScreen from "./_conversationScreens/LongTextScreen";
import MultiChoiceScreen from "./_conversationScreens/MultiChoiceScreen";
import NumberScreen from "./_conversationScreens/NumberScreen";
import OpenEndedScreen from "./_conversationScreens/OpenEndedScreen";
import OpinionScaleScreen from "./_conversationScreens/OpinionScaleScreen";
import PhoneScreen from "./_conversationScreens/PhoneScreen";
import ShortTextScreen from "./_conversationScreens/ShortTextScreen";
import StatementScreen from "./_conversationScreens/StatementScreen";
import WebsiteScreen from "./_conversationScreens/WebsiteScreen";
import RatingScreen from "./_conversationScreens/RatingScreen";
import GlobalLoadingScreen from "../../molecules/GlobalLoadingScreen";
import MediaDisplay from "../../molecules/MediaDisplay";
import NPSScreen from "./_conversationScreens/NPSScreen";
import MultiQuestionPageScreen from "./_conversationScreens/MultiQuestionPageScreen";
import SchedulerScreen from "./_conversationScreens/SchedulerScreen";

interface Props {
  currentStep: FormJourneyItem,
  leadForm: LeadForm,
  editorMode: boolean,
  previewMode?: boolean,
  wrapperId?: string,
  currentStepOverride: string | null,
  children: React.ReactNode;
  position: 'before' | 'after' | 'active';
  evaluationIsProcessing: boolean;
  viewed?: boolean;
  resetForm: () => void;
  createConversation: () => Promise<Conversation>;
  withLogoSpacing?: boolean;
  requiresGDPRConsent?: string;
  handleAnswer?: () => void;
  minimizeContentSize?: boolean
  onContentClick?: (fieldName: string, blockId: string) => void;
  hiddenFields?: {
    field: string;
    value: string;
  }[];
}

const ScreenRenderer = ({
  currentStep,
  leadForm,
  editorMode,
  previewMode,
  onContentClick,
  currentStepOverride,
  children,
  position,
  evaluationIsProcessing,
  resetForm,
  createConversation,
  viewed,
  withLogoSpacing,
  requiresGDPRConsent,
  wrapperId = 'conversation-wrapper',
  hiddenFields,
  handleAnswer,
  minimizeContentSize
}: Props) => {
  const [isTitleHidden, setIsTitleHidden] = useState(false);
  const [titleText, setTitleText] = useState('');
  const screenRendererRef = useRef<HTMLDivElement>(null);
  const contentInnerRef = useRef<HTMLDivElement>(null);

  // Split / detached media-position classes shrink the content column to 50%
  // so it sits beside the background media. With no media that leaves left-
  // aligned content stuck to the viewport edge — fall back to FULL so the
  // centered max-width content column (contentInner) is used instead.
  const layoutMediaPosition = currentStep.media?.link
    ? currentStep.mediaPosition
    : 'FULL';

  useEffect(() => {
    const scrollContainer = screenRendererRef.current;
    if (!scrollContainer) return;

    let observer: IntersectionObserver | null = null;

    const setup = () => {
      const titleEl = scrollContainer.querySelector('h1') || scrollContainer.querySelector('h4');
      if (!titleEl) return;

      setTitleText(titleEl.textContent || '');

      observer = new IntersectionObserver(
        ([entry]) => setIsTitleHidden(!entry.isIntersecting),
        { root: scrollContainer, threshold: 0 }
      );

      observer.observe(titleEl);
    };

    const frameId = requestAnimationFrame(setup);

    return () => {
      cancelAnimationFrame(frameId);
      observer?.disconnect();
      setIsTitleHidden(false);
    };
  }, [currentStep]);

  const screenKey = `${currentStep?.blockId}_${wrapperId}_item`;
  const commonProps = {
    id: currentStep?.blockId,
    editorMode: !!editorMode,
    previewMode: !!previewMode,
    currentStepOverride,
    leadForm,
    active: position === 'active',
    requiresGDPRConsent,
    hiddenFields,
    step: currentStep && {
      ...currentStep,
      selectOptions: currentStep.selectOptions
        ? (currentStep.selectOptions as JourneySelectOption[]).map(o => JourneySelectOption.parse(o))
        : null
    },
    onContentClick
  };

  const renderContent = () => {
    switch (currentStep?.type) {
      case 'WELCOME_SCREEN':
        return (
          <ConversationWelcomeScreen
            key={screenKey}
            {...commonProps}
            type="welcome"
            about={leadForm.about}
            purpose={leadForm.overview}
            tone={leadForm.tone}
            snippet={leadForm.snippets?.map(snippet => snippet.content) ?? []}
            title={currentStep.content}
            caption={currentStep.secondaryContent}
          />
        );
      case 'OPEN_ENDED':
        return (
          <OpenEndedScreen
            key={screenKey}
            {...commonProps}
            evaluationIsProcessing={evaluationIsProcessing}
            createConversation={createConversation}
          />
        )
      case 'EMAIL':
        return <EmailScreen key={screenKey} {...commonProps} />;
      case 'STATEMENT':
        return <StatementScreen key={screenKey} {...commonProps} handleAnswer={handleAnswer} />;
      case 'PHONE_NUMBER':
        return <PhoneScreen key={screenKey} {...commonProps} />;
      case 'WEBSITE':
        return <WebsiteScreen key={screenKey} {...commonProps} />;
      case 'NUMBER':
        return <NumberScreen key={screenKey} {...commonProps} />;
      case 'FILE':
        return <FileScreen key={screenKey} {...commonProps} />;
      case 'SHORT_TEXT':
        return <ShortTextScreen key={screenKey} {...commonProps} />;
      case 'LONG_TEXT':
        return <LongTextScreen key={screenKey} {...commonProps} />;
      case 'MULTIPLE_CHOICE':
        return <MultiChoiceScreen key={screenKey} {...commonProps} handleAnswer={handleAnswer} />;
      case 'DROPDOWN':
        return <DropdownScreen key={screenKey} {...commonProps} handleAnswer={handleAnswer} />;
      case 'OPTION_SCALE':
        return <OpinionScaleScreen key={screenKey} {...commonProps} handleAnswer={handleAnswer} />;
      case 'NPS':
        return <NPSScreen key={screenKey} {...commonProps} handleAnswer={handleAnswer} />;
      case 'RATING':
        return <RatingScreen key={screenKey} {...commonProps} handleAnswer={handleAnswer} />;
      case 'CONTACT_INFO':
        return <ContactInfoScreen key={screenKey} {...commonProps} />;
      case 'ADDRESS':
        return <AddressScreen key={screenKey} {...commonProps} />;
      case 'DATE':
        return <DateScreen key={screenKey} {...commonProps} />;
      case 'MULTI_QUESTION_PAGE':
        return <MultiQuestionPageScreen key={screenKey} {...commonProps} />;
      case 'END_SCREEN':
        return (
          <ConversationExitScreen
            key={screenKey}
            {...commonProps}
            exitCallToAction={currentStep.thirdContent ?? 'Continue'}
            exitCallToActionLink={currentStep.forthContent ?? null}
            leadForm={leadForm}
            editorMode={!!editorMode}
            title={currentStep.content}
            caption={currentStep.secondaryContent}
            resetForm={resetForm}
            onContentClick={onContentClick}
          />
        );
      case 'REDIRECT_SCREEN':
        return (
          <ConversationRedirectScreen
            key={screenKey}
            id={currentStep.blockId}
            leadForm={leadForm}
            url={currentStep.content}
            paramsRaw={currentStep.secondaryContent}
            editorMode={!!editorMode}
            previewMode={!!previewMode}
            onContentClick={onContentClick}
            hiddenFields={hiddenFields}
          />
        );
      default:
        return <GlobalLoadingScreen />;
    }
  }

  return (
    <div
      id={`${currentStep?.blockId}-wrapper`}
      ref={screenRendererRef}
      data-horizontal-transition={leadForm.transitionType === 'SLIDE_HORIZONTAL' ? '' : undefined}
      className={cn(
        'screenRenderer',
        leadForm.type?.toLowerCase() ?? 'multi_step',
        (layoutMediaPosition === 'INLINE_LEFT' || layoutMediaPosition === 'INLINE_RIGHT') && 'wideLayout',
        withLogoSpacing &&
        (currentStep.mobileMediaPosition !== 'MOBILE_POSTER_TOP' || !currentStep.media?.link) &&
        'withLogoSpacing',
        withLogoSpacing && leadForm.logoPosition?.startsWith('BOTTOM') && 'logoAtBottom',
        `c-content-${currentStep.contentAlignment.toLowerCase()}`,
        (
          currentStep.contentAlignment === 'CENTER' ||
          currentStep?.type === 'WELCOME_SCREEN' ||
          currentStep?.type === 'END_SCREEN' ||
          currentStep?.type === 'REDIRECT_SCREEN'
        ) &&
          'centered',
        position !== 'active' && 'disabled',
        editorMode && 'editorMode',
        minimizeContentSize && 'minimizeContentSize',
        // Lets the MQP-specific CSS swap the .contentInner centering mechanism
        // from `margin: auto` to `justify-content: safe center` without
        // relying on `:has()` selectors (which are flaky under hot-reload).
        currentStep?.type === 'MULTI_QUESTION_PAGE' && 'mqp-active',
        currentStep?.type === 'SCHEDULER' && 'scheduler-step',
        `media-position-${layoutMediaPosition}`,
      )}
    >
      <div className="stickyTitleHeader">
        <div className={cn(
          'stickyTitleInner',
          isTitleHidden && position === 'active' && 'stickyTitleVisible'
        )}>
          <span className="stickyTitleText">{titleText}</span>
        </div>
      </div>
      <div
        ref={contentInnerRef}
        className={cn(
          'contentInner',
          (
            currentStep.mediaPosition === 'INLINE_RIGHT' ||
            currentStep.mediaPosition === 'INLINE_LEFT'
          ) && 'horizontalLayout',
          currentStep.mediaPosition === 'INLINE_RIGHT' && 'inlineRight',
          (currentStep.mobileMediaPosition === 'MOBILE_POSTER_TOP' && currentStep.media?.link) && 'mobilePoster'
        )}
      >
        {
          currentStep.media &&
          (
            currentStep.mediaPosition === 'INLINE_CENTER' ||
            currentStep.mediaPosition === 'INLINE_LEFT' ||
            currentStep.mediaPosition === 'INLINE_RIGHT' ||
            (
              (
                currentStep.mobileMediaPosition === 'MOBILE_INLINE_CENTER' ||
                currentStep.mobileMediaPosition === 'MOBILE_INLINE_TOP'
              ) &&
              // INLINE_BELOW_TITLE renders its own in-flow block on these
              // mobile positions — the top slot would double the media
              currentStep.mediaPosition !== 'INLINE_BELOW_TITLE'
            ) ||
            currentStep.mobileMediaPosition === 'MOBILE_POSTER_TOP'
          )
          && (
            <div className={cn(
              'inlineMedia',
              currentStep.mobileMediaPosition === 'MOBILE_FULL' && 'hideOnMobile',
              (
                currentStep.mediaPosition === 'FULL' ||
                currentStep.mediaPosition === 'DETATCHED_LEFT' ||
                currentStep.mediaPosition === 'DETATCHED_RIGHT' ||
                currentStep.mediaPosition === 'SPLIT_LEFT' ||
                currentStep.mediaPosition === 'SPLIT_RIGHT' ||
                currentStep.mediaPosition === 'INLINE_BELOW_TITLE'
              ) && 'hideOnDesktop',
              (currentStep.mobileMediaPosition === 'MOBILE_POSTER_TOP' && currentStep.media?.link) && 'mobilePoster',
              (
                currentStep.mobileMediaPosition === 'MOBILE_INLINE_CENTER' ||
                currentStep.mobileMediaPosition === 'MOBILE_INLINE_TOP'
              ) && 'mobileInline',
            )}>
              <MediaDisplay
                inline
                mobilePoster={!!(currentStep.mobileMediaPosition === 'MOBILE_POSTER_TOP' && currentStep.media?.link)}
                mediOpacity={currentStep.opacity ?? 60}
                noTranslate
                media={currentStep.media}
              />
            </div>
          )
        }
        <div className="content">
          {currentStep?.type === 'SCHEDULER' ? (
            <SchedulerScreen
              key={screenKey}
              {...commonProps}
              handleAnswer={handleAnswer}
              footer={children}
            />
          ) : (
            <>
              {renderContent()}
              {
                currentStep.media &&
                currentStep.mediaPosition === 'INLINE_BELOW_TITLE' && (
                  // In-flow media between the question title and the answer
                  // area (Typeform's "stack" layout). CSS flex ordering in
                  // conversation-wrapper.css moves it under the title block.
                  <div className={cn(
                    'inlineMedia',
                    'belowTitleMedia',
                    (
                      currentStep.mobileMediaPosition === 'MOBILE_FULL' ||
                      currentStep.mobileMediaPosition === 'MOBILE_POSTER_TOP'
                    ) && 'hideOnMobile',
                  )}>
                    <MediaDisplay
                      inline
                      mediOpacity={currentStep.opacity ?? 60}
                      noTranslate
                      media={currentStep.media}
                    />
                  </div>
                )
              }
              <div className="footerWrapper">
                {children}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
};

export default ScreenRenderer;